import React, { useState, useEffect, ChangeEvent } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  CircularProgress,
  Box,
  Typography,
  FormHelperText,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  MenuItem,
  InputLabel,
  Select,
} from '@mui/material';
import { LocalizationProvider, DesktopDatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import { format, parse, isValid } from 'date-fns';
import { Promotion, getClientVariables, ClientVariable, getPromotionById } from '../../services/promotionService';
import ImageUpload from '../common/ImageUpload';
import { uploadPromoImage, deletePromoImage } from '../../utils/storage';

interface FormData extends Omit<Promotion, 'start_date' | 'end_date'> {
  start_date: Date | null;
  end_date: Date | null;
}

interface EditPromotionDialogProps {
  open: boolean;
  promotion: Promotion | null;
  onClose: () => void;
  onSave: (promotion: Partial<Promotion>, imageFile?: File) => Promise<void>;
}

export const EditPromotionDialog: React.FC<EditPromotionDialogProps> = ({
  open,
  promotion,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState<FormData>({
    id: 0,
    title: '',
    img: '',
    code: '',
    client_points: 0,
    consumer_limit: 0,
    client_limit: 0,
    start_date: null,
    end_date: null,
    status: 'inactivo',
    terms: '',
    channel: '',
    gec: '',
    recipients: '',
  });
  
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [recipientType, setRecipientType] = useState<'segment' | 'client'>('segment');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [clientVariables, setClientVariables] = useState<{ channels: ClientVariable[]; gec: ClientVariable[] }>({ channels: [], gec: [] });
  const [loadingVariables, setLoadingVariables] = useState(true);
  const [loadingPromotion, setLoadingPromotion] = useState(false);
  const [currentPromotion, setCurrentPromotion] = useState<Promotion | null>(null);

  // Load promotion data from API when dialog opens
  useEffect(() => {
    const loadPromotionData = async () => {
      if (promotion && open && promotion.id) {
        setLoadingPromotion(true);
        try {
          console.log('Loading promotion data for ID:', promotion.id);
          const promotionData = await getPromotionById(promotion.id);
          console.log('Loaded promotion data from API:', promotionData);
          setCurrentPromotion(promotionData);
        } catch (error) {
          console.error('Error loading promotion data:', error);
          // Fallback to the promotion prop if API call fails
          setCurrentPromotion(promotion);
        } finally {
          setLoadingPromotion(false);
        }
      }
    };

    if (open && promotion) {
      loadPromotionData();
    }
  }, [promotion, open]);

  // Initialize form data when current promotion is loaded
  useEffect(() => {
    if (currentPromotion && !loadingPromotion) {
      // Convert ISO date strings to Date objects
      const parseDate = (dateString: string | null): Date | null => {
        if (!dateString) return null;
        try {
          // First try to parse as ISO string (from API)
          const date = new Date(dateString);
          if (!isNaN(date.getTime())) return date;
          
          // If that fails, try to parse as dd/MM/yyyy (from existing data)
          return parse(dateString, 'dd/MM/yyyy', new Date());
        } catch (e) {
          console.error('Error parsing date:', dateString, e);
          return null;
        }
      };

      const initialData = {
        ...currentPromotion,
        start_date: parseDate(currentPromotion.start_date),
        end_date: parseDate(currentPromotion.end_date),
        code: currentPromotion.code || '',
        client_points: currentPromotion.client_points || 0,
        consumer_limit: currentPromotion.consumer_limit || 0,
        client_limit: currentPromotion.client_limit || 0,
        terms: currentPromotion.terms || '',
        channel: currentPromotion.channel || '',
        gec: currentPromotion.gec || '',
        recipients: currentPromotion.recipients || '',
      };
      
      console.log('Initializing form data with fresh promotion data:', currentPromotion);
      console.log('Terms from API:', currentPromotion.terms);
      console.log('Initial data terms:', initialData.terms);
      console.log('Image from API:', currentPromotion.img);
      console.log('Initial data img:', initialData.img);
      
      setFormData(initialData);
      
      // Set recipient type based on available data
      if (initialData.recipients && !initialData.channel && !initialData.gec) {
        setRecipientType('client');
      } else {
        setRecipientType('segment');
      }
    }
  }, [currentPromotion, loadingPromotion]);

  // Additional useEffect to debug and ensure terms field is properly initialized
  useEffect(() => {
    console.log('FormData updated:', formData);
    console.log('Terms value:', formData.terms);
    console.log('Image value:', formData.img);
  }, [formData.terms, formData.img]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setFormData({
        id: 0,
        title: '',
        img: '',
        code: '',
        client_points: 0,
        consumer_limit: 0,
        client_limit: 0,
        start_date: null,
        end_date: null,
        status: 'inactivo',
        terms: '',
        channel: '',
        gec: '',
        recipients: '',
      });
      setImageFile(null);
      setErrors({});
      setCurrentPromotion(null);
      setLoadingPromotion(false);
    }
  }, [open]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { target: { name: string; value: string } }) => {
    const { name, value } = e.target;
    
    // Handle numeric fields
    if (['client_points', 'consumer_limit', 'client_limit'].includes(name)) {
      setFormData(prev => ({
        ...prev,
        [name]: value === '' ? '' : parseInt(value) || 0
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  
  const handleImageChange = async (file: File | null) => {
    if (!file) {
      setFormData(prev => ({
        ...prev,
        img: '',
      }));
      setImageFile(null);
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setErrors(prev => ({
        ...prev,
        image: 'Por favor sube un archivo de imagen válido',
      }));
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({
        ...prev,
        image: 'La imagen no debe superar los 5MB',
      }));
      return;
    }

    setImageFile(file);
    
    // Create a preview URL for the image
    const previewUrl = URL.createObjectURL(file);
    setFormData(prev => ({
      ...prev,
      img: previewUrl,
    }));
    
    // Clear any previous image error
    if (errors.image) {
      setErrors(prev => ({
        ...prev,
        image: '',
      }));
    }
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      status: e.target.checked ? 'activo' : 'inactivo',
    }));
  };
  
  const handleRecipientTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newType = e.target.value as 'segment' | 'client';
    setRecipientType(newType);
    
    // Clear channel/gec when switching to client mode and vice versa
    if (newType === 'client') {
      setFormData(prev => ({ ...prev, channel: '', gec: '' }));
    } else {
      setFormData(prev => ({ ...prev, recipients: '' }));
    }
  };

  const handleDateChange = (field: 'start_date' | 'end_date') => (date: Date | null) => {
    setFormData(prev => ({
      ...prev,
      [field]: date
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.title?.trim()) {
      newErrors.title = 'El título es requerido';
    }
    
    if (!formData.start_date) {
      newErrors.start_date = 'La fecha de inicio es requerida';
    }
    
    if (!formData.end_date) {
      newErrors.end_date = 'La fecha de fin es requerida';
    } else if (formData.start_date && formData.end_date < formData.start_date) {
      newErrors.end_date = 'La fecha de fin debe ser posterior a la fecha de inicio';
    }
    
    if (!formData.terms?.trim()) {
      newErrors.terms = 'Los términos y condiciones son requeridos';
    }
    
    if (recipientType === 'segment') {
      if (!formData.channel?.trim()) newErrors.channel = 'El canal es requerido';
      if (!formData.gec?.trim()) newErrors.gec = 'El GEC es requerido';
    } else if (!formData.recipients?.trim()) {
      newErrors.recipients = 'Debe ingresar al menos un ID de cliente';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      let imgUrl = formData.img;
      
      // Upload new image if selected
      if (imageFile) {
        // If there was a previous image, delete it
        if (formData.img && formData.img.startsWith('http')) {
          try {
            await deletePromoImage(formData.img);
          } catch (err) {
            console.error('Error deleting old image:', err);
            // Continue with upload even if delete fails
          }
        }
        
        // Upload new image
        imgUrl = await uploadPromoImage(imageFile);
      }
      
      // Prepare the promotion data for the API
      const promotionData: Partial<Promotion> = {
        ...formData,
        img: imgUrl,
        // Convert dates to the format expected by the API (YYYY-MM-DD)
        start_date: formData.start_date ? format(formData.start_date, 'yyyy-MM-dd') : '',
        end_date: formData.end_date ? format(formData.end_date, 'yyyy-MM-dd') : '',
      };
      
      // Call the onSave prop with the promotion data and the image file
      // Pass the imageFile as a second parameter if it exists
      if (imageFile) {
        await onSave(promotionData, imageFile);
      } else {
        await onSave(promotionData);
      }
      
      // Close the dialog on success
      onClose();
    } catch (error) {
      console.error('Error saving promotion:', error);
      setErrors({
        form: 'Ocurrió un error al guardar la promoción. Por favor, inténtalo de nuevo.'
      });
    } finally {
      setLoading(false);
    }
  };

  // Load client variables on component mount
  useEffect(() => {
    const loadClientVariables = async () => {
      try {
        setLoadingVariables(true);
        const data = await getClientVariables();
        setClientVariables(data);
      } catch (error) {
        console.error('Error loading client variables:', error);
      } finally {
        setLoadingVariables(false);
      }
    };

    if (open) {
      loadClientVariables();
    }
  }, [open]);

  if (!promotion) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Editar Promoción</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {loadingPromotion && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Cargando datos de la promoción...</Typography>
            </Box>
          )}
          
          {errors.form && (
            <Typography color="error" gutterBottom>
              {errors.form}
            </Typography>
          )}
          
          {!loadingPromotion && (
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, mt: 2 }}>
            <Box sx={{ flex: 1 }}>
              
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <TextField
                  name="title"
                  label="Título *"
                  fullWidth
                  margin="normal"
                  value={formData.title}
                  onChange={handleChange}
                  error={!!errors.title}
                  helperText={errors.title || ' '}
                  required
                  inputProps={{
                    'aria-required': 'true',
                    'aria-invalid': !!errors.title
                  }}
                  sx={{ flex: 2 }}
                />
                
                <TextField
                  name="code"
                  label="Código"
                  fullWidth
                  margin="normal"
                  value={formData.code}
                  onChange={handleChange}
                  helperText="Opcional"
                  sx={{ flex: 1 }}
                />
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 2 }}>
                <TextField
                  name="client_points"
                  label="Puntos por cliente"
                  type="number"
                  fullWidth
                  margin="normal"
                  value={formData.client_points}
                  onChange={handleChange}
                  error={!!errors.client_points}
                  helperText={errors.client_points}
                  InputProps={{
                    inputProps: { min: 0 }
                  }}
                  sx={{ flex: 1 }}
                />
                
                <TextField
                  name="consumer_limit"
                  label="Límite por consumidor"
                  type="number"
                  fullWidth
                  margin="normal"
                  value={formData.consumer_limit}
                  onChange={handleChange}
                  error={!!errors.consumer_limit}
                  helperText={errors.consumer_limit}
                  InputProps={{
                    inputProps: { min: 0 }
                  }}
                  sx={{ flex: 1 }}
                />
                
                <TextField
                  name="client_limit"
                  label="Límite por cliente"
                  type="number"
                  fullWidth
                  margin="normal"
                  value={formData.client_limit}
                  onChange={handleChange}
                  error={!!errors.client_limit}
                  helperText={errors.client_limit}
                  InputProps={{
                    inputProps: { min: 0 }
                  }}
                  sx={{ flex: 1 }}
                />
              </Box>
              
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, my: 2 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                  <DesktopDatePicker
                    label="Fecha de inicio *"
                    value={formData.start_date}
                    onChange={handleDateChange('start_date')}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        margin: 'normal',
                        error: !!errors.start_date,
                        helperText: errors.start_date,
                      }
                    }}
                  />
                </LocalizationProvider>
                
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                  <DesktopDatePicker
                    label="Fecha de fin *"
                    value={formData.end_date}
                    onChange={handleDateChange('end_date')}
                    minDate={formData.start_date || undefined}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        margin: 'normal',
                        error: !!errors.end_date,
                        helperText: errors.end_date,
                      }
                    }}
                  />
                </LocalizationProvider>
              </Box>
              
              <FormControl 
                component="fieldset" 
                margin="normal" 
                fullWidth 
                error={!!errors.channel || !!errors.gec || !!errors.recipients}
                required
              >
                <FormLabel component="legend">Destinatarios *</FormLabel>
                <RadioGroup
                  row
                  aria-label="tipo de destinatario"
                  name="recipientType"
                  value={recipientType}
                  onChange={handleRecipientTypeChange}
                  aria-required="true"
                >
                  <FormControlLabel value="segment" control={<Radio />} label="Segmento" />
                  <FormControlLabel value="client" control={<Radio />} label="Clientes" />
                </RadioGroup>
                {recipientType === 'segment' ? (
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 2 }}>
                    <FormControl fullWidth margin="normal" error={!!errors.channel}>
                      <InputLabel id="channel-label">Canal *</InputLabel>
                      <Select
                        labelId="channel-label"
                        name="channel"
                        value={formData.channel}
                        onChange={handleChange}
                        label="Canal *"
                        disabled={loadingVariables}
                      >
                        <MenuItem value="">
                          <em>Seleccionar canal</em>
                        </MenuItem>
                        {clientVariables.channels.map((channel) => (
                          <MenuItem key={channel.id} value={channel.id}>
                            {channel.id}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.channel && <FormHelperText>{errors.channel}</FormHelperText>}
                    </FormControl>
                    
                    <FormControl fullWidth margin="normal" error={!!errors.gec}>
                      <InputLabel id="gec-label">GEC *</InputLabel>
                      <Select
                        labelId="gec-label"
                        name="gec"
                        value={formData.gec}
                        onChange={handleChange}
                        label="GEC *"
                        disabled={loadingVariables}
                      >
                        <MenuItem value="">
                          <em>Seleccionar GEC</em>
                        </MenuItem>
                        {clientVariables.gec.map((gec) => (
                          <MenuItem key={gec.id} value={gec.id}>
                            {gec.id}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.gec && <FormHelperText>{errors.gec}</FormHelperText>}
                    </FormControl>
                  </Box>
                ) : (
                  <TextField
                    name="recipients"
                    label="IDs de clientes *"
                    fullWidth
                    margin="normal"
                    value={formData.recipients}
                    onChange={handleChange}
                    error={!!errors.recipients}
                    helperText={errors.recipients || 'Ingrese IDs separados por comas'}
                    required
                    inputProps={{
                      'aria-required': 'true',
                      'aria-invalid': !!errors.recipients
                    }}
                  />
                )}
              </FormControl>
              
              <TextField
                name="terms"
                label="Términos y condiciones *"
                fullWidth
                multiline
                rows={4}
                margin="normal"
                value={formData.terms || ''}
                onChange={handleChange}
                error={!!errors.terms}
                helperText={errors.terms || ' '}
                required
                inputProps={{
                  'aria-required': 'true',
                  'aria-invalid': !!errors.terms
                }}
              />
              

            </Box>
            
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center',
              mt: { xs: 2, md: 0 },
              width: { xs: '100%', md: 250 },
              flexShrink: 0
            }}>
              <Typography variant="subtitle2" gutterBottom>
                Imagen de la promoción
              </Typography>
              <ImageUpload
                onChange={handleImageChange}
                label="Cambiar imagen"
                initialImage={formData.img}
              />
              <Box sx={{ mt: 0.5 }}>
                <Typography variant="caption" color="textSecondary" display="block" sx={{ fontSize: '0.75rem' }}>
                  Formatos permitidos: webp, png, jpg
                </Typography>
                <Typography variant="caption" color="textSecondary" display="block" sx={{ fontSize: '0.75rem' }}>
                  Peso máximo: 1MB
                </Typography>
              </Box>
              {errors.image && (
                <FormHelperText error sx={{ mt: 1 }}>{errors.image}</FormHelperText>
              )}
              
              <Box mt={3} width="100%" textAlign="center">
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.status === 'activo'}
                      onChange={handleStatusChange}
                      color="primary"
                    />
                  }
                  label={formData.status === 'activo' ? 'Activo' : 'Inactivo'}
                  labelPlacement="start"
                  sx={{ 
                    margin: 0,
                    '& .MuiFormControlLabel-label': {
                      marginLeft: 1,
                      fontWeight: 'medium'
                    }
                  }}
                />
                
                <Box mt={4} display="flex" flexDirection="column" gap={2}>
                  <Button 
                    onClick={onClose} 
                    disabled={loading}
                    variant="outlined"
                    fullWidth
                  >
                    Cancelar
                  </Button>
                  <Button 
                    type="submit" 
                    variant="contained" 
                    color="primary" 
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : null}
                    fullWidth
                  >
                    {loading ? 'Guardando...' : 'Guardar Cambios'}
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
          )}
        </DialogContent>
      </form>
    </Dialog>
  );
};

export default EditPromotionDialog;
